/**
 * FilesPanel — Presentational panel for reservation-linked files (BookingDetailModal)
 *
 * Charter: documentation/cursor/Charters/CodeAuditor.md v1.0.1
 * Phase 5b-1 extracted from BookingDetailModal.jsx; JSX moved verbatim. No logic changes.
 *
 * Purpose:
 *   Render the Files expandable panel: loading/error+retry/empty/list of files.
 *   Presentational only; parent owns state, effects, and fetch. All data and onRetry passed as props.
 *
 * Assumptions & Invariants:
 *   - No fetch; no hooks; no new logic. Only props in, JSX out.
 *   - Parent owns files state, filesLoading, filesError, onClose (setTopPanelOpen(null)), onRetry (clear error + trigger re-fetch).
 *   - formatDateTimeET is passed from parent (dateTimeET) so panel stays dependency-free.
 *
 * Dependencies: React only
 *
 * Version: 1.0.0
 *
 * Changelog:
 *   2026-01-27 Phase 5b-1 extraction from BookingDetailModal (files panel JSX only).
 */

export default function FilesPanel({
  theme,
  onClose,
  filesLoading,
  filesError,
  files,
  onRetry,
  formatDateTimeET,
}) {
  return (
    <div style={{
      borderTop: `1px solid ${theme === 'dark' ? 'rgba(255, 255, 255, 0.15)' : 'rgba(200, 195, 188, 0.4)'}`,
      padding: '16px 24px',
      backgroundColor: theme === 'dark'
        ? 'rgba(255, 255, 255, 0.02)'
        : 'rgba(0, 0, 0, 0.01)',
    }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '8px',
      }}>
        <p style={{
          fontSize: '14px',
          fontWeight: 'var(--font-weight-medium)',
          color: theme === 'dark' ? 'var(--color-text-secondary-dark)' : 'var(--color-text-secondary)',
          margin: 0,
        }}>
          Files
        </p>
        <button
          onClick={onClose}
          style={{
            border: 'none',
            background: 'transparent',
            cursor: 'pointer',
            fontSize: '12px',
            color: theme === 'dark' ? 'var(--color-text-secondary-dark)' : 'var(--color-text-secondary)',
            padding: '4px 8px',
            borderRadius: '4px',
            transition: 'background-color 0.2s ease',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = theme === 'dark'
              ? 'rgba(255, 255, 255, 0.1)'
              : 'rgba(0, 0, 0, 0.05)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
          }}
        >
          Close
        </button>
      </div>
      {filesLoading ? (
        <p style={{
          fontSize: '14px',
          color: theme === 'dark' ? 'var(--color-text-secondary-dark)' : 'var(--color-text-secondary)',
          margin: 0,
          opacity: 0.7,
        }}>
          Loading files...
        </p>
      ) : filesError ? (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
        }}>
          <p style={{
            fontSize: '14px',
            color: theme === 'dark' ? '#ff6b6b' : '#d32f2f',
            margin: 0,
            fontWeight: 'var(--font-weight-medium)',
          }}>
            {filesError}
          </p>
          <button
            onClick={onRetry}
            style={{
              padding: '6px 12px',
              fontSize: '12px',
              backgroundColor: theme === 'dark'
                ? 'rgba(255, 255, 255, 0.1)'
                : 'rgba(0, 0, 0, 0.05)',
              border: `1px solid ${theme === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.1)'}`,
              borderRadius: '4px',
              cursor: 'pointer',
              color: theme === 'dark' ? 'var(--color-text-primary-dark)' : 'var(--color-text-primary)',
            }}
          >
            Retry
          </button>
        </div>
      ) : files && files.length > 0 ? (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '12px',
        }}>
          {files.map((file) => (
            <div
              key={file.id}
              style={{
                padding: '12px',
                backgroundColor: theme === 'dark'
                  ? 'rgba(255, 255, 255, 0.03)'
                  : 'rgba(0, 0, 0, 0.02)',
                borderRadius: '6px',
                border: `1px solid ${theme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)'}`,
              }}
            >
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                gap: '12px',
              }}>
                <div style={{ flex: 1 }}>
                  <a
                    href={file.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      fontSize: '14px',
                      fontWeight: 'var(--font-weight-medium)',
                      color: theme === 'dark' ? '#60A5FA' : '#3B82F6',
                      textDecoration: 'none',
                      display: 'block',
                      marginBottom: file.comment ? '4px' : '0',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.textDecoration = 'underline';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.textDecoration = 'none';
                    }}
                  >
                    {file.name || file.url}
                  </a>
                  {file.comment && (
                    <p style={{
                      fontSize: '13px',
                      color: theme === 'dark' ? 'var(--color-text-secondary-dark)' : 'var(--color-text-secondary)',
                      margin: 0,
                      opacity: 0.8,
                    }}>
                      {file.comment}
                    </p>
                  )}
                  {file.created_at && (
                    <p style={{
                      fontSize: '12px',
                      color: theme === 'dark' ? 'var(--color-text-secondary-dark)' : 'var(--color-text-secondary)',
                      margin: '4px 0 0 0',
                      opacity: 0.7,
                    }}>
                      {formatDateTimeET(file.created_at, { dateOnly: false })}
                    </p>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p style={{
          fontSize: '14px',
          color: theme === 'dark' ? 'var(--color-text-secondary-dark)' : 'var(--color-text-secondary)',
          margin: 0,
          opacity: 0.7,
        }}>
          No files are available for this reservation.
        </p>
      )}
    </div>
  );
}
